import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  serverTimestamp,
  setDoc,
  Timestamp,
  where,
} from 'firebase/firestore';

/**
 * Fathom ↔ company-meeting-report glue.
 *
 * The Fathom webhook (functions/fathomWebhook.js) writes every recording to
 * `meeting_summaries`. Client calls resolve to an account; internal meetings — including
 * the company meeting run from Meeting Mode — land with `accountId: null`, and the webhook
 * deliberately creates no tasks for them. This module links those internal recordings to
 * the `meetingReports` doc Meeting Mode saved on Finish, and turns the recording's action
 * items into CRM tasks for internal users, using the SAME dedupe keys and deterministic
 * task doc IDs the webhook uses so the two paths can never double-create a task.
 */

const HOUR = 60 * 60 * 1000;

// How far a recording's start may sit from the report's Finish timestamp to count as the
// same meeting. Finish is clicked at the END of the meeting, so the recording normally
// started a while before it; allow a little slack the other way for an early Finish.
const MATCH_BEFORE_MS = 5 * HOUR;
const MATCH_AFTER_MS = 1 * HOUR;

// Fathom processes a recording minutes after the call ends, so its `createdAt` trails the
// report's. The query window is wide on purpose; scoring narrows it down.
const QUERY_BEFORE_MS = 6 * HOUR;
const QUERY_AFTER_MS = 36 * HOUR;

const COMPANY_MEETING_PATTERNS = [
  /company\s*meeting/i,
  /team\s*meeting/i,
  /staff\s*meeting/i,
  /all[\s-]*hands/i,
  /weekly/i,
  /meeting\s*mode/i,
  /\bbba\b/i,
];

export const toJsDate = (value) => {
  try {
    if (!value) return null;
    if (typeof value.toDate === 'function') return value.toDate();
    if (value instanceof Date) return Number.isNaN(value.getTime()) ? null : value;
    if (typeof value === 'number') return new Date(value);
    if (typeof value === 'string') {
      const d = new Date(value);
      return Number.isNaN(d.getTime()) ? null : d;
    }
    if (typeof value.seconds === 'number') return new Date(value.seconds * 1000);
    return null;
  } catch {
    return null;
  }
};

export const actionItemText = (item) =>
  String(typeof item === 'string' ? item : item?.description || item?.text || '').trim();

export function fathomRecordingUrl(summary) {
  if (!summary) return null;
  if (summary.recordingUrl) return summary.recordingUrl;
  if (summary.shareUrl) return summary.shareUrl;
  if (summary.recordingId != null && summary.recordingId !== '') {
    return `https://fathom.video/calls/${summary.recordingId}`;
  }
  return null;
}

export function isInternalRecording(summary) {
  return !!summary && summary.source === 'fathom' && summary.accountId == null;
}

export function recordingStartDate(summary) {
  return toJsDate(summary?.scheduledStartTime) || toJsDate(summary?.createdAt);
}

export function looksLikeCompanyMeeting(summary) {
  const title = String(summary?.meetingTitle || '');
  return COMPANY_MEETING_PATTERNS.some((p) => p.test(title));
}

/**
 * Lower is better; null means "not this meeting". Time distance in ms, with a 90-minute
 * penalty for titles that don't read like the company meeting so a same-day 1:1 can't
 * outrank it.
 */
export function scoreRecordingForReport(summary, reportDate) {
  const start = recordingStartDate(summary);
  if (!start || !reportDate) return null;
  const delta = reportDate.getTime() - start.getTime();
  if (delta > MATCH_BEFORE_MS || delta < -MATCH_AFTER_MS) return null;
  return Math.abs(delta) + (looksLikeCompanyMeeting(summary) ? 0 : 90 * 60 * 1000);
}

/** Internal (account-less) Fathom recordings whose webhook write landed inside [from, to]. */
export async function loadInternalRecordingsBetween(db, from, to) {
  const snap = await getDocs(
    query(
      collection(db, 'meeting_summaries'),
      where('createdAt', '>=', Timestamp.fromDate(from)),
      where('createdAt', '<=', Timestamp.fromDate(to))
    )
  );
  return snap.docs
    .map((d) => ({ id: d.id, ...d.data() }))
    .filter(isInternalRecording)
    .sort((a, b) => (recordingStartDate(b)?.getTime() || 0) - (recordingStartDate(a)?.getTime() || 0));
}

/** Best internal recording for a report saved at `reportDate`, or null. */
export async function findRecordingForReport(db, reportDate) {
  if (!reportDate) return null;
  const candidates = await loadInternalRecordingsBetween(
    db,
    new Date(reportDate.getTime() - QUERY_BEFORE_MS),
    new Date(reportDate.getTime() + QUERY_AFTER_MS)
  );
  let best = null;
  let bestScore = Infinity;
  for (const candidate of candidates) {
    const score = scoreRecordingForReport(candidate, reportDate);
    if (score != null && score < bestScore) {
      best = candidate;
      bestScore = score;
    }
  }
  return best;
}

/** The small, stable pointer stored on the meetingReports doc. */
export function buildFathomLink(summary, { linkedBy } = {}) {
  const start = recordingStartDate(summary);
  return {
    summaryId: summary.id,
    recordingId: summary.recordingId ?? null,
    recordingUrl: fathomRecordingUrl(summary),
    meetingTitle: summary.meetingTitle || '',
    scheduledStartTime: start ? start.toISOString() : null,
    recorderUid: summary.recorderUid || null,
    actionItemCount: Array.isArray(summary.actionItems) ? summary.actionItems.length : 0,
    linkedAt: new Date().toISOString(),
    linkedBy: linkedBy || 'auto',
  };
}

export async function loadMeetingSummary(db, summaryId) {
  if (!summaryId) return null;
  const snap = await getDoc(doc(db, 'meeting_summaries', summaryId));
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
}

const normalizeEmail = (email) => String(email || '').trim().toLowerCase();

export function matchInternalUserByEmail(email, internalUsers = []) {
  const target = normalizeEmail(email);
  if (!target) return null;
  return (
    internalUsers.find((u) => {
      const candidates = [u.email, u.workEmail, u.contactEmail, u.userEmail]
        .concat(Array.isArray(u.alternateEmails) ? u.alternateEmails : [])
        .map(normalizeEmail)
        .filter(Boolean);
      return candidates.includes(target);
    }) || null
  );
}

async function sha256Hex(input) {
  const text = String(input);
  if (typeof crypto !== 'undefined' && crypto.subtle && typeof TextEncoder !== 'undefined') {
    const digest = await crypto.subtle.digest('SHA-256', new TextEncoder().encode(text));
    return Array.from(new Uint8Array(digest))
      .map((b) => b.toString(16).padStart(2, '0'))
      .join('');
  }
  // Insecure-context fallback (no WebCrypto): still deterministic, just not the webhook's hash.
  let h1 = 0x811c9dc5;
  let h2 = 0x01000193;
  for (let i = 0; i < text.length; i++) {
    const c = text.charCodeAt(i);
    h1 = Math.imul(h1 ^ c, 0x01000193) >>> 0;
    h2 = Math.imul(h2 ^ c, 0x811c9dc5) >>> 0;
  }
  return `fallback_${h1.toString(16).padStart(8, '0')}${h2.toString(16).padStart(8, '0')}`;
}

/**
 * Mirrors createFathomTasks() in functions/fathomWebhook.js exactly:
 *   dedupe key   = sha256(`${recordingId}|${email}|${normalizedDescription}`)
 *   content key  = sha256(`${accountId}|${meetingDay || recordingId}|${email}|${contentText}`)
 * `accountId` is null for internal meetings, which the template literal renders as "null" —
 * the same string the webhook produces.
 */
export async function buildFathomTaskKeys({ summary, item }) {
  const recordingId = summary?.recordingId ?? '';
  const accountId = summary?.accountId ?? null;
  const assigneeEmail = typeof item === 'object' ? item?.assigneeEmail || '' : '';
  const description = actionItemText(item);

  const normalizedEmail = normalizeEmail(assigneeEmail);
  const normalizedDescription = description.toLowerCase().replace(/\s+/g, ' ');
  const meetingDay = String(summary?.scheduledStartTime || '').slice(0, 10);
  const contentText = normalizedDescription.replace(/[^\w\s]/g, '').replace(/\s+/g, ' ').trim();

  const [fathomDedupeKey, fathomContentKey] = await Promise.all([
    sha256Hex(`${recordingId}|${normalizedEmail}|${normalizedDescription}`),
    sha256Hex(`${accountId}|${meetingDay || recordingId}|${normalizedEmail}|${contentText}`),
  ]);
  return { fathomDedupeKey, fathomContentKey, taskDocId: `fathom_${fathomContentKey}` };
}

/** Every task already created from this recording (by the webhook or by a report). */
export async function loadTasksForRecording(db, recordingId) {
  if (recordingId == null || recordingId === '') return [];
  const snap = await getDocs(
    query(collection(db, 'tasks'), where('fathomRecordingId', '==', recordingId))
  );
  return snap.docs.map((d) => ({ id: d.id, ...d.data() }));
}

const normalizeTitle = (text) => String(text || '').trim().toLowerCase().replace(/\s+/g, ' ').slice(0, 300);

/** Find the task that already represents an action item, by content key first, then title. */
export function findTaskForActionItem(item, keys, tasks = []) {
  if (keys?.fathomContentKey) {
    const byKey = tasks.find(
      (t) => t.id === keys.taskDocId || t.fathomContentKey === keys.fathomContentKey
    );
    if (byKey) return byKey;
  }
  if (keys?.fathomDedupeKey) {
    const byDedupe = tasks.find((t) => t.fathomDedupeKey === keys.fathomDedupeKey);
    if (byDedupe) return byDedupe;
  }
  const title = normalizeTitle(actionItemText(item));
  if (!title) return null;
  return tasks.find((t) => normalizeTitle(t.title) === title) || null;
}

/**
 * Create the CRM task for one action item, assigned to an internal user. Returns
 * { id, existed } — `existed` when a task with the same content key already lives in
 * Firestore (webhook race, double click, second report linked to the same recording).
 */
export async function createInternalTaskFromActionItem(
  db,
  { summary, item, assignee, createdBy, meetingReportId, dueDate = null }
) {
  const description = actionItemText(item);
  if (!description) throw new Error('Action item has no text');
  if (!assignee?.id) throw new Error('Pick an internal user to assign this action item to');

  const keys = await buildFathomTaskKeys({ summary, item });
  const ref = doc(db, 'tasks', keys.taskDocId);
  const existing = await getDoc(ref);
  if (existing.exists()) return { id: ref.id, existed: true, keys };

  const assigneeEmail = typeof item === 'object' ? item?.assigneeEmail || '' : '';
  const start = recordingStartDate(summary);
  const meetingLabel = `${summary?.meetingTitle || 'Company meeting'}${
    start ? ` on ${start.toLocaleDateString()}` : ''
  }`;

  const payload = {
    title: description.slice(0, 300),
    description: `Action item from the ${meetingLabel}${assigneeEmail ? ` (Fathom assignee: ${assigneeEmail})` : ''}`,
    assignees: [assignee.id],
    assigneeScope: 'internal',
    createdBy: createdBy || null,
    dueDate: dueDate || null,
    priority: 'medium',
    status: 'open',
    category: 'general',
    completed: false,
    accountId: null,
    // Tasks.js keys internal work on companyId 'internal' (BBA emblem, no account link).
    companyId: 'internal',
    companyName: 'Internal',
    source: 'fathom',
    department: summary?.department || null,
    files: [],
    fathomDedupeKey: keys.fathomDedupeKey,
    fathomContentKey: keys.fathomContentKey,
    fathomRecordingId: summary?.recordingId ?? null,
    fathomSummaryId: summary?.id || null,
    meetingReportId: meetingReportId || null,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
  };

  await setDoc(ref, payload);
  return { id: ref.id, existed: false, keys, payload };
}
